import { useState } from "react";
import {
  CheckCircle2, Circle, Repeat, Clock, Plus, Trash2, ChevronDown, ChevronUp,
  Sparkles, ShieldCheck,
  BellRing, Tag,
} from "lucide-react";
import { PARTNER_SUPPORT_OPTIONS, TASK_CATEGORIES, categoryLabel, supportLabel } from "@/lib/taskMeta";

export const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const DOW_JS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export function todayISO() { return new Date().toISOString().slice(0, 10); }
export function todayDow() { return DOW_JS[new Date().getDay()]; }

export function scheduledToday(task) {
  if (!task) return false;
  if (task.type === "oneoff") return !(task.completions || []).length && !task.done;
  return Array.isArray(task.days) && task.days.includes(todayDow());
}
export function completionsLast7(task) {
  const set = new Set(task.completions || []);
  const vset = new Set(task.verifications || []);
  const out = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(); d.setDate(d.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    const dow = DOW_JS[d.getDay()];
    const scheduled = task.type === "oneoff" ? true : (task.days || []).includes(dow);
    out.push({ iso, dow, done: set.has(iso), verified: vset.has(iso), scheduled });
  }
  return out;
}
export function completionRate7(task) {
  const days = completionsLast7(task).filter((d) => d.scheduled);
  if (days.length === 0) return 0;
  return Math.round((days.filter((d) => d.done).length / days.length) * 100);
}

export function ImportanceStars({ value, onChange, testId }) {
  const levels = [
    { value: 1, label: "Low" },
    { value: 2, label: "Medium" },
    { value: 3, label: "High" },
  ];
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      {levels.map((level) => (
        <button
          key={level.value} type="button"
          onClick={() => onChange && onChange(level.value)}
          className={`px-2 py-1 rounded-sm border text-[9px] uppercase tracking-[0.16em] ${
            level.value === value
              ? "border-[#F59E0B]/50 bg-[#F59E0B]/10 text-[#F59E0B]"
              : "border-white/10 text-[#7a7a80]"
          } ${onChange ? "hover:border-[#F59E0B]/30" : ""} transition-colors`}
          data-testid={testId ? `${testId}-${level.value}` : undefined}
          disabled={!onChange}
        >
          {level.label}
        </button>
      ))}
    </div>
  );
}

/**
 * TaskRow — universal task display
 * mode: 'self' (I can toggle done + delete)  |  'partner' (I verify their done)  |  'readonly'
 */
export function TaskRow({ task, mode = "self", onToggle, onDelete, onVerify, onNudge, extraActions, stakesEnabled = false }) {
  const [expanded, setExpanded] = useState(false);
  const doneToday = (task.completions || []).includes(todayISO());
  const verifiedToday = (task.verifications || []).includes(todayISO());
  const verificationDate = [...(task.completions || [])]
    .filter((date) => !(task.verifications || []).includes(date))
    .sort()
    .at(-1);
  const isScheduled = scheduledToday(task);
  const rate = completionRate7(task);

  return (
    <li
      className={`p-3 rounded-md border transition-colors ${
        doneToday
          ? "border-[#F59E0B]/40 bg-[#F59E0B]/[0.04]"
          : isScheduled
          ? "border-white/10 bg-[#0f0f0f]"
          : "border-white/5 bg-white/[0.02]"
      }`}
      data-testid={`task-row-${task.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Primary toggle */}
        {mode === "self" ? (
          <button
            onClick={() => onToggle && onToggle(task.id)}
            data-testid={`task-toggle-${task.id}`}
            className={`shrink-0 mt-0.5 ${doneToday ? "text-[#F59E0B] hover:scale-110" : "text-[#A1A1AA] hover:text-[#F5F5F0] hover:scale-110"} transition-transform`}
            title={doneToday ? "Unmark today" : "Mark done today"}
          >
            {doneToday ? <CheckCircle2 className="w-5 h-5" strokeWidth={1.75} /> : <Circle className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        ) : (
          <div className="shrink-0 mt-0.5" title={doneToday ? "Partner marked done today" : "Not marked today"}>
            {doneToday ? <CheckCircle2 className="w-5 h-5 text-[#F59E0B]" strokeWidth={1.75} /> : <Circle className="w-5 h-5 text-white/20" strokeWidth={1.5} />}
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2 flex-wrap">
            <span className={`text-sm ${doneToday ? "line-through text-[#7a7a80]" : "text-[#F5F5F0]"}`}>{task.text}</span>
            {task.type === "recurring" ? (
              <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-white/10 text-[#A1A1AA] inline-flex items-center gap-1">
                <Repeat className="w-2.5 h-2.5" /> {task.days?.length === 7 ? "Daily" : task.days?.join(" ")}
              </span>
            ) : (
              <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-white/10 text-[#A1A1AA]">One-off</span>
            )}
            {task.imported && (
              <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-[#F59E0B]/30 bg-[#F59E0B]/5 text-[#F59E0B]">Imported</span>
            )}
            {task.owner === "shared" && (
              <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]">Shared</span>
            )}
            <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-sm border border-sky-400/20 bg-sky-400/5 text-sky-200 inline-flex items-center gap-1">
              <Tag className="w-2.5 h-2.5" /> {categoryLabel(task.category)}
            </span>
            {mode === "self" && doneToday && (
              <span
                className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1 ${
                  verifiedToday
                    ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : "border border-white/10 text-[#7a7a80]"
                }`}
                title={verifiedToday ? "Verified by partner" : "Awaiting partner verification"}
              >
                <ShieldCheck className="w-2.5 h-2.5" />
                {verifiedToday ? "Verified" : "Pending"}
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-3 text-[11px] text-[#A1A1AA] flex-wrap">
            {task.time && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {task.time}
                {task.flexible ? <em className="text-[#7a7a80] not-italic">(flexible)</em> : <span className="text-[#F59E0B] uppercase text-[9px] tracking-[0.18em]">fixed</span>}
              </span>
            )}
            <span>{task.duration || 60}m</span>
            <ImportanceStars value={task.importance || 2} />
            {stakesEnabled && <span className="text-[9px] uppercase tracking-[0.14em] text-[#F59E0B]">
              Miss = {task.missCreditValue || task.importance || 2} credits
            </span>}
            <span className="ml-auto flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#F59E0B]" /> {rate}% last 7d
            </span>
          </div>
          {(task.partnerSupport || []).length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[9px] uppercase tracking-[0.16em] text-[#7a7a80]">Partner:</span>
              {task.partnerSupport.map((item) => (
                <span key={item} className="text-[10px] px-2 py-0.5 rounded-full border border-violet-400/20 bg-violet-400/5 text-violet-200">
                  {supportLabel(item)}
                </span>
              ))}
            </div>
          )}
          {mode === "self" && task.nudgedToday && (
            <div className="mt-2 text-[10px] text-[#F59E0B] inline-flex items-center gap-1">
              <BellRing className="w-3 h-3" /> Your partner nudged this today
            </div>
          )}

          {expanded && (
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA] mb-2">Last 7 days</div>
              <div className="flex items-end gap-1.5">
                {completionsLast7(task).map((d) => (
                  <div key={d.iso} className="flex-1 flex flex-col items-center gap-1" title={`${d.iso} · ${d.done ? (d.verified ? "verified" : "done") : d.scheduled ? "missed" : "not scheduled"}`}>
                    <div className={`w-full h-6 rounded-sm ${
                      d.done && d.verified ? "bg-emerald-500" :
                      d.done ? "bg-[#F59E0B]" :
                      d.scheduled ? "bg-white/[0.06] border border-dashed border-white/10" :
                      "bg-transparent border border-white/[0.03]"
                    }`} />
                    <span className="text-[9px] text-[#A1A1AA] uppercase">{d.dow.slice(0, 1)}</span>
                  </div>
                ))}
              </div>
              {mode === "self" && (
                <div className="mt-2 text-[10px] text-[#A1A1AA] flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#F59E0B]" /> Done</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Verified</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm border border-dashed border-white/10" /> Missed</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          {mode === "partner" && onNudge && !doneToday && (
            <button
              onClick={() => onNudge(task.id)}
              disabled={task.nudgedToday}
              className={`p-1.5 rounded-md border transition-colors ${task.nudgedToday ? "border-[#F59E0B]/20 text-[#F59E0B]/50" : "border-white/10 text-[#A1A1AA] hover:text-[#F59E0B] hover:border-[#F59E0B]/40"}`}
              data-testid={`task-nudge-${task.id}`}
              aria-label={task.nudgedToday ? "Already nudged today" : "Nudge partner"}
              title={task.nudgedToday ? "Already nudged today" : "Send a gentle nudge"}
            >
              <BellRing className="w-3.5 h-3.5" />
            </button>
          )}
          {mode === "partner" && onVerify && (
            <button
              onClick={() => onVerify(task.id, verificationDate)}
              data-testid={`task-verify-${task.id}`}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.18em] border transition-colors ${
                !verificationDate
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                  : "border-white/10 text-[#A1A1AA] hover:border-white/20 hover:text-[#F5F5F0]"
              }`}
              disabled={!verificationDate}
              title={verificationDate ? `Verify the completion from ${verificationDate}` : "No unverified completions"}
            >
              <ShieldCheck className="w-3 h-3" />
              {verificationDate ? `Verify ${verificationDate.slice(5)}` : "Verified"}
            </button>
          )}
          {extraActions && extraActions(task)}
          <button
            onClick={() => setExpanded((e) => !e)}
            className="text-[#7a7a80] hover:text-[#F5F5F0]"
            data-testid={`task-expand-${task.id}`}
            aria-label="Show history"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {mode === "self" && onDelete && (
            <button onClick={() => onDelete(task.id)} className="text-[#7a7a80] hover:text-[#F5F5F0]" data-testid={`task-delete-${task.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

export function TaskForm({ onSubmit, onCancel, showOwner = true, initial, stakesEnabled = false }) {
  const [form, setForm] = useState({
    text: "", owner: "me", type: "recurring",
    days: ["Mon", "Tue", "Wed", "Thu", "Fri"],
    time: "", duration: 60, flexible: true, importance: 2,
    category: "other", partnerSupport: [],
    ...initial,
  });

  const toggleDay = (d) => setForm((f) => ({
    ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d],
  }));
  const toggleSupport = (value) => setForm((f) => ({
    ...f,
    partnerSupport: f.partnerSupport.includes(value)
      ? f.partnerSupport.filter((item) => item !== value)
      : [...f.partnerSupport, value],
  }));

  const submit = () => {
    if (!form.text.trim()) return;
    onSubmit({
      ...form,
      text: form.text.trim(),
      time: form.time || null,
      duration: Number(form.duration) || 60,
      importance: Number(form.importance) || 2,
    });
  };

  return (
    <div className="p-5 rounded-md border border-white/10 bg-white/[0.02] mb-5" data-testid="task-form">
      <div className={`grid ${showOwner ? "sm:grid-cols-[1fr_120px]" : "sm:grid-cols-1"} gap-3 mb-4`}>
        <input
          type="text" value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
          placeholder="Hit the gym · Study DSA · Read 30 pages…"
          className="hm-input w-full px-4 py-2.5 rounded-md text-sm font-light"
          data-testid="task-input"
        />
        {showOwner && (
          <select value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} className="hm-input px-3 py-2.5 rounded-md text-sm" data-testid="task-owner">
            <option value="me">Mine</option>
            <option value="shared">Shared</option>
          </select>
        )}
      </div>

      <div className="flex items-center gap-2 mb-4">
        {["recurring", "oneoff"].map((tp) => (
          <button
            key={tp} type="button" onClick={() => setForm({ ...form, type: tp })}
            data-testid={`task-type-${tp}`}
            className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider border ${
              form.type === tp ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F5F5F0]" : "border-white/10 text-[#A1A1AA]"
            }`}
          >
            {tp === "recurring" ? <><Repeat className="w-3 h-3 inline-block mr-1" /> Recurring</> : "One-off"}
          </button>
        ))}
      </div>

      {form.type === "recurring" && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Days of the week</div>
          <div className="flex flex-wrap gap-2" data-testid="task-days">
            {DOW.map((d) => (
              <button
                key={d} type="button" onClick={() => toggleDay(d)}
                className={`w-14 py-1.5 rounded-md border text-xs uppercase tracking-wider ${
                  form.days.includes(d) ? "border-[#F59E0B] bg-[#F59E0B] text-black font-semibold" : "border-white/10 text-[#A1A1AA]"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Preferred time</div>
          <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="hm-input w-full px-3 py-2.5 rounded-md text-sm" data-testid="task-time" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Duration (min)</div>
          <input type="number" min={5} max={480} step={5} value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="hm-input w-full px-3 py-2.5 rounded-md text-sm" data-testid="task-duration" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Timing</div>
          <div className="flex items-center gap-2 pt-2">
            <button type="button" onClick={() => setForm({ ...form, flexible: !form.flexible })} data-testid="task-flexible"
              className={`px-3 py-1.5 rounded-md text-xs border ${form.flexible ? "border-[#F59E0B] bg-[#F59E0B]/10 text-[#F59E0B]" : "border-white/10 text-[#A1A1AA]"}`}>
              {form.flexible ? "Flexible" : "Fixed"}
            </button>
            <span className="text-[10px] text-[#7a7a80] font-light">{form.flexible ? "Any time today" : "At the set time"}</span>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-[220px_1fr] gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">Category</div>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="hm-input w-full px-3 py-2.5 rounded-md text-sm" data-testid="task-category">
            {TASK_CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <p className="mt-1.5 text-[10px] text-[#7a7a80]">Category progress powers your Flex badges.</p>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">What should your partner do?</div>
          <div className="flex flex-wrap gap-2" data-testid="task-partner-support">
            {PARTNER_SUPPORT_OPTIONS.map(([value, label]) => {
              const active = form.partnerSupport.includes(value);
              return <button key={value} type="button" onClick={() => toggleSupport(value)} aria-pressed={active} className={`px-2.5 py-1.5 rounded-md border text-xs transition-colors ${active ? "border-violet-400/50 bg-violet-400/10 text-violet-100" : "border-white/10 text-[#A1A1AA] hover:border-white/20"}`}>{label}</button>;
            })}
          </div>
        </div>
      </div>

      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">
          Importance{stakesEnabled ? " · determines credits lost for a miss" : " · helps you and your partner prioritize"}
        </div>
        <ImportanceStars value={form.importance} onChange={(v) => setForm({ ...form, importance: v })} testId="task-importance" />
      </div>

      <div className="flex items-center gap-2">
        <button onClick={submit} data-testid="task-submit" className="hm-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold">
          <Plus className="w-4 h-4" /> Add task
        </button>
        {onCancel && <button onClick={onCancel} className="hm-btn-ghost inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs">Cancel</button>}
      </div>
    </div>
  );
}
